<template>
  <div class="filters-wrapper">
    <!-- Data Range com Datepicker -->
    <div class="filter">
      <DatePicker
        ref="datepicker"
        v-model="tempDateRange"
        range
        format="DD/MM/YYYY"
        :enable-time-picker="false"
        :partial-range="true"
        :auto-apply="false"
        :min-date="minSelectableDate"
        :max-date="maxSelectableDate"
        :disabled-date="isDateDisabled"
        :multi-calendars="false"
      >
        <template #input>
          <button class="filter-button" @click="openCalendar">
            {{ formattedDateRange }}
          </button>
        </template>
      </DatePicker>
    </div>

    <!-- Marcadores -->
    <div class="filter">
      <div class="custom-dropdown">
        <button class="filter-button" @click="toggleDropdown('label')">
          {{
            selectedLabels.length
              ? $t('LABELS_REPORTS.COMMON.SELECTED_COUNT', {
                  count: selectedLabels.length,
                })
              : $t('LABELS_REPORTS.FILTERS.ALL_LABELS')
          }}
        </button>
        <div v-if="openDropdown === 'label'" class="dropdown-content">
          <input
            type="text"
            v-model="labelSearch"
            class="search-input"
            :placeholder="$t('LABELS_REPORTS.FILTERS.TYPE_TO_SEARCH')"
          />
          <div
            v-for="label in filteredLabels"
            :key="label.id"
            class="checkbox-option"
          >
            <label>
              <input
                type="checkbox"
                :value="label.name"
                v-model="selectedLabels"
                @change="emitFilters"
              />
              {{ label.name }}
            </label>
            <span class="count">{{ label.countFormatted }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Caixa de entrada -->
    <div class="filter">
      <div class="custom-dropdown">
        <button class="filter-button" @click="toggleDropdown('inbox')">
          {{
            selectedInboxes.length
              ? $t('LABELS_REPORTS.FILTERS.SELECTED_INBOXES', {
                  count: selectedInboxes.length,
                })
              : $t('LABELS_REPORTS.FILTERS.INBOX')
          }}
        </button>
        <div v-if="openDropdown === 'inbox'" class="dropdown-content">
          <input
            type="text"
            v-model="inboxSearch"
            class="search-input"
            :placeholder="$t('LABELS_REPORTS.FILTERS.INBOX_SEARCH_PLACEHOLDER')"
          />
          <div
            v-for="inbox in filteredInboxes"
            :key="inbox.name"
            class="checkbox-option"
          >
            <label>
              <input
                type="checkbox"
                :value="inbox.name"
                v-model="selectedInboxes"
                @change="emitFilters"
              />
              {{ inbox.name }}
            </label>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import DatePicker from 'vue2-datepicker';
import 'vue2-datepicker/index.css';

export default {
  name: 'LabelsFilters',
  components: {
    DatePicker,
  },
  props: {
    labelsData: {
      type: Array,
      required: true,
    },
  },
  data() {
    const today = new Date();
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(today.getDate() - 7);

    return {
      tempDateRange: [sevenDaysAgo, today],
      customDateRange: [sevenDaysAgo, today],
      selectedLabels: [],
      selectedInboxes: [],
      labelSearch: '',
      inboxSearch: '',
      openDropdown: null,
    };
  },
  computed: {
    labels() {
      const unique = {};
      return this.labelsData
        .map(item => ({ name: item.tag_name }))
        .filter(label => {
          if (!label.name || unique[label.name]) return false;
          unique[label.name] = true;
          return true;
        });
    },
    inboxes() {
      const unique = {};
      return this.labelsData
        .map(item => ({ name: item.inbox_name }))
        .filter(inbox => {
          if (!inbox.name || unique[inbox.name]) return false;
          unique[inbox.name] = true;
          return true;
        });
    },
    filteredLabels() {
      const counts = {};
      this.labelsData.forEach(item => {
        const name = item.tag_name;
        if (name) counts[name] = (counts[name] || 0) + 1;
      });

      return this.labels
        .filter(l =>
          l.name.toLowerCase().includes(this.labelSearch.toLowerCase())
        )
        .map(l => ({
          ...l,
          count: counts[l.name] || 0,
          countFormatted: this.formatCount(counts[l.name] || 0),
        }));
    },
    filteredInboxes() {
      return this.inboxes.filter(i =>
        i.name.toLowerCase().includes(this.inboxSearch.toLowerCase())
      );
    },
    formattedDateRange() {
      const [start, end] = this.tempDateRange;
      if (!start || !end)
        return this.$t('LABELS_REPORTS.FILTERS.SELECT_DATE_RANGE');
      const format = d =>
        new Date(d).toLocaleDateString('pt-BR', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        });
      return `${format(start)} ~ ${format(end)}`;
    },
    today() {
      return new Date();
    },
    minSelectableDate() {
      const oneYearAgo = new Date();
      oneYearAgo.setFullYear(this.today.getFullYear() - 1);
      return oneYearAgo;
    },
    maxSelectableDate() {
      return this.today;
    },
  },
  watch: {
    tempDateRange(newRange) {
      if (newRange[0] && newRange[1]) {
        this.customDateRange = newRange;

        this.selectedLabels = [];
        this.selectedInboxes = [];
        this.labelSearch = '';
        this.inboxSearch = '';
        this.openDropdown = null;

        this.emitFilters();
      }
    },
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutside);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside);
  },
  methods: {
    emitFilters() {
      this.$emit('filter-change', {
        customDateRange: this.customDateRange,
        labelNames: this.selectedLabels,
        inboxNames: this.selectedInboxes,
      });
    },
    toggleDropdown(dropdownName) {
      this.openDropdown =
        this.openDropdown === dropdownName ? null : dropdownName;
    },
    openCalendar() {
      this.$refs.datepicker.openMenu?.();
    },
    formatCount(value) {
      if (value >= 1000) return `${(value / 1000).toFixed(1)} mil`;
      return `${value}`;
    },
    handleClickOutside(event) {
      const wrapper = this.$el;
      if (!wrapper || !wrapper.contains(event.target)) {
        this.openDropdown = null;
      }
    },
    isDateDisabled(date) {
      const min = this.minSelectableDate;
      const max = this.maxSelectableDate;
      return date < min || date > max;
    },
  },
};
</script>

<style>
.filters-wrapper {
  width: 280px;
  min-height: 150vh;
  background-color: #000;
  padding: 20px;
  box-sizing: border-box;
  color: white;
  display: flex;
  flex-direction: column;
  gap: 20px;
  flex-shrink: 0;
}

.filter {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.filter-button {
  width: 240px;
  height: 50px;
  background: black !important;
  color: white !important;
  border: 1px solid white !important;
  padding: 10px;
  font-size: 14px;
  font-weight: 500;
  border-radius: 4px;
  cursor: pointer;
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  box-sizing: border-box;
  appearance: none;
}

.filter-button:hover {
  background: #1a1a1a;
}

.hidden-datepicker-input {
  display: none !important;
}

::v-deep(.dp__input_icon) {
  display: none !important;
}

.custom-dropdown {
  position: relative;
  width: 240px;
}

.dropdown-content {
  position: absolute;
  top: 56px;
  background: #000;
  border: 1px solid white;
  width: 100%;
  max-height: 300px;
  overflow-y: auto;
  z-index: 999;
  padding: 10px;
  border-radius: 4px;
  box-shadow: 0 0 10px rgba(255, 255, 255, 0.1);
}

.search-input {
  width: 100%;
  padding: 6px;
  margin-bottom: 10px;
  border-radius: 4px;
  border: 1px solid #ccc;
  box-sizing: border-box;
}

.checkbox-option {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 0;
  gap: 8px;
}

.checkbox-option label {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  color: white;
}

.checkbox-option input[type='checkbox'] {
  margin: 0;
}

.count {
  color: #bbb;
  font-size: 12px;
  white-space: nowrap;
}

.mx-icon-calendar {
  display: none !important;
}

.mx-icon-clear {
  display: none !important;
}
</style>
